import { type ReactNode } from 'react'
import { ErrorBoundary } from '@/src/renderer/components/common/ErrorBoundary'
import { ActiveSettingsPageProvider } from './ActiveSettingsPageContext'
import './PreferencesPageSlot.scss'

interface PreferencesPageSlotProps {
  /** Stable identifier of the slot, e.g. `"app-experience/appearance"`. Used
   *  as the ErrorBoundary key so a crashed slot resets when reactivated. */
  slotId: string
  /** Currently active slot? Drives the `display: none/block` toggle plus
   *  `aria-hidden` / `inert` for assistive tech. */
  isActive: boolean
  /** Has this slot ever been active during the lifetime of the shell?
   *  Lazy mount — content is rendered only after the first activation. */
  wasActivated: boolean
  /** The page to render once the slot has been activated. */
  children: ReactNode
}

/**
 * Persistent slot wrapper used by {@link PreferencesShell}. Replicates the
 * TabStack `TabSlot` pattern:
 *
 * - `display: none` for inactive slots (preserves React state and `scrollTop`
 *   across reactivation; avoids the GPU "ghost layer" lag of `visibility: hidden`)
 * - `inert` + `aria-hidden` block keyboard / screen-reader access while hidden
 * - Lazy mount: content renders only after the first activation, then stays
 *   mounted for the lifetime of the shell
 * - Per-slot {@link ErrorBoundary} keyed on `slotId` so a crash in one
 *   SubPage doesn't take down the rest of the Settings shell
 * - {@link ActiveSettingsPageProvider} exposes `isActive` to children via
 *   {@link useIsActiveSettingsPage} for visibility-gated side effects
 *
 * Per-slot scroll lives inside the inner `__scroll` wrap — keeps each
 * SubPage's scroll position independent and survives `display: none`
 * toggles (Chromium preserves `scrollTop` on hidden subtrees).
 */
export function PreferencesPageSlot({
  slotId,
  isActive,
  wasActivated,
  children,
}: PreferencesPageSlotProps) {
  return (
    <div
      className={`preferences-page-slot${isActive ? ' preferences-page-slot--active' : ''}`}
      aria-hidden={!isActive}
      inert={!isActive}
    >
      <div className="preferences-page-slot__scroll">
        <ErrorBoundary key={slotId} name={`SettingsPage:${slotId}`} variant="inline">
          {wasActivated && (
            <ActiveSettingsPageProvider value={isActive}>
              {children}
            </ActiveSettingsPageProvider>
          )}
        </ErrorBoundary>
      </div>
    </div>
  )
}
